import { Loader2 } from "lucide-react";
import { shouldShowRiskFlowDirectionalForecast } from "../../lib/riskflow-feed";
import { ivHeatColor } from "../../types/agent-desk";
import { FintheonNumber } from "../numbers/FintheonNumber";
import { FadingRuler } from "../shared/FadingRuler";
import {
  formatTimelineDate,
  formatTimelineRelative,
  timelineMacroColor,
  type TimelineFeedItem,
} from "./timeline-model";

interface TimelineFeedProps {
  activeColor: string;
  dateGroups: [string, TimelineFeedItem[]][];
  error: string | null;
  loading: boolean;
  showDirectionalScoring: boolean;
}

export function TimelineFeed(props: TimelineFeedProps) {
  if (props.loading) {
    return (
      <div
        className="flex flex-1 items-center justify-center py-12"
        aria-label="Loading timeline"
      >
        <Loader2 className="h-5 w-5 animate-spin text-[var(--fintheon-accent)]" />
      </div>
    );
  }
  if (props.dateGroups.length === 0) {
    return (
      <p className="flex-1 px-5 py-12 text-center text-[12px] text-[var(--fintheon-muted)]/48">
        {props.error ?? "No items in this timeline"}
      </p>
    );
  }
  return (
    <div className="min-h-0 flex-1 overflow-y-auto px-4 py-2">
      {props.dateGroups.map(([date, events], groupIndex) => (
        <section key={date} className={groupIndex === 0 ? "" : "pt-2"}>
          {groupIndex === 0 ? null : (
            <FadingRuler className="mb-3 opacity-45" />
          )}
          <div className="mb-1 flex items-center gap-2 px-1">
            <span
              className="font-mono text-[9px] font-semibold uppercase tracking-[0.12em]"
              style={{ color: props.activeColor }}
            >
              {formatTimelineDate(date)}
            </span>
            <span className="ml-auto font-mono text-[9px] text-[var(--fintheon-muted)]/36">
              {events.length}
            </span>
          </div>
          {events.map((item, index) => (
            <TimelineRow
              key={item.id}
              item={item}
              showDirectionalScoring={props.showDirectionalScoring}
              showRuler={index > 0}
            />
          ))}
        </section>
      ))}
    </div>
  );
}

function TimelineRow({
  item,
  showDirectionalScoring,
  showRuler,
}: {
  item: TimelineFeedItem;
  showDirectionalScoring: boolean;
  showRuler: boolean;
}) {
  const sentiment =
    item.priceBrainScore?.sentiment?.toLowerCase() ??
    item.sentiment ??
    "bearish";
  const isBullish = sentiment === "bullish";
  const showForecast = shouldShowRiskFlowDirectionalForecast({
    ...item,
    ivScore: item.ivScore,
    showDirectionalScoring,
  });
  return (
    <article className="group">
      {showRuler ? <FadingRuler className="mx-1 opacity-30" /> : null}
      <div className="px-1 py-2.5">
        <div className="flex items-start gap-2">
          <span
            className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full"
            style={{ backgroundColor: timelineMacroColor(item.macroLevel) }}
          />
          <p className="flex-1 text-[12px] font-medium leading-[1.4] text-[var(--fintheon-text)]">
            {item.title || item.headline}
          </p>
          <div className="flex shrink-0 items-center gap-1.5">
            {item.ivScore == null ? null : (
              <span
                className="font-mono text-[9px] font-bold tabular-nums"
                style={{ color: ivHeatColor(Number(item.ivScore)) }}
              >
                IV{" "}
                <FintheonNumber
                  value={Number(item.ivScore)}
                  format={{
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                  }}
                  kind="score"
                  surface="timeline-feed"
                />
              </span>
            )}
            {showForecast ? (
              <span
                className="text-[11px] font-bold"
                style={{
                  color: isBullish
                    ? "var(--fintheon-bullish)"
                    : "var(--fintheon-bearish)",
                }}
              >
                {isBullish ? "▲" : "▼"}
              </span>
            ) : null}
          </div>
        </div>
        {item.tags?.length ? (
          <div className="ml-3.5 mt-1 flex flex-wrap gap-x-2 gap-y-0.5">
            {item.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="font-mono text-[8px] text-[var(--fintheon-muted)]/42"
              >
                #{tag}
              </span>
            ))}
          </div>
        ) : null}
        <div className="ml-3.5 mt-1 flex items-center gap-2 font-mono text-[8px] text-[var(--fintheon-muted)]/32">
          <span>{formatTimelineRelative(item.publishedAt)}</span>
          {item.source ? <span>{item.source}</span> : null}
        </div>
      </div>
    </article>
  );
}
